import { useLingui } from "@lingui/react";
import type { MobilityAd } from "@mobility-item-page/types";
import type { ReactNode } from "react";
import { useAdViewAd } from "src/context/AdViewProviderContext.tsx";
import { BenefitTabs, MemberAssociationTabs, type SafetyTabs } from "src/types/safetyElements.ts";
import { Icon } from "../Icon.tsx";
import Condition from "./elements/Condition.tsx";
import DeclarationOfGoods from "./elements/DeclarationOfGoods.tsx";
import ExchangePolicy from "./elements/ExchangePolicy.tsx";
import HomeDelivery from "./elements/HomeDelivery.js";
import Premium from "./elements/Premium.tsx";
import ServiceHistory from "./elements/ServiceHistory.tsx";
import ServicePlanFollowed from "./elements/ServicePlanFollowed.tsx";
import TradeOrg from "./elements/TradeOrg.tsx";
import UsedCarWarrantyPurchased from "./elements/UsedCarWarranty.tsx";
import Warranty from "./elements/Warranty.tsx";
import type { SafetyElementsProps } from "./Index.tsx";

const useTabMap = (
  ad: MobilityAd,
): Record<
  SafetyTabs,
  {
    id: SafetyTabs;
    trackingName: string;
    trackingId: string;
    label: string;
    icon: ReactNode;
    content: ReactNode;
  }
> => {
  const { i18n } = useLingui();

  return {
    [BenefitTabs.Warranty]: {
      id: BenefitTabs.Warranty,
      trackingName: "Warranty tab",
      trackingId: "MO_ev42",
      label: i18n.t({
        id: "itemPageLayout.safetyElement.tab.warranty.label",
        message: "Warranty",
      }),
      icon: <Icon name="Warranty" size="medium" />,
      content: <Warranty ad={ad} />,
    },
    [BenefitTabs.UsedCarWarrantyPurchased]: {
      id: BenefitTabs.UsedCarWarrantyPurchased,
      trackingName: "Used car warranty tab",
      trackingId: "MO_ev62",
      label: i18n.t({
        id: "itemPageLayout.safetyElement.tab.usedCarWarrantyPurchased.label",
        message: "Used car warranty",
      }),
      icon: <Icon name="Warranty" size="medium" />,
      content: <UsedCarWarrantyPurchased />,
    },
    [BenefitTabs.DeclarationOfGoods]: {
      id: BenefitTabs.DeclarationOfGoods,
      trackingName: "Condition tab",
      trackingId: "MO_ev64",
      label: i18n.t({
        id: "itemPageLayout.safetyElement.tab.declarationOfGoods.label",
        message: "Declaration of goods",
      }),
      icon: <Icon name="ActiveAds" size="medium" />,
      content: <DeclarationOfGoods ad={ad} />,
    },
    [BenefitTabs.ServiceHistory]: {
      id: BenefitTabs.ServiceHistory,
      trackingName: "Service tab",
      trackingId: "MO_ev43", // Sharing tracking event with ServicePlanFollowed (we'll never have both fields at the same time, ServiceHistory is just used for Blocket)
      label: i18n.t({
        id: "itemPageLayout.safetyElement.tab.service.label",
        message: "Service",
      }),
      icon: <Icon name="Wrench" size="medium" />,
      content: <ServiceHistory ad={ad} />,
    },
    [BenefitTabs.ServicePlanFollowed]: {
      id: BenefitTabs.ServicePlanFollowed,
      trackingName: "Service tab",
      trackingId: "MO_ev43", // Sharing tracking event with ServiceHistory (we'll never have both fields at the same time, ServicePlanFollowed is just used for Finn)
      label: i18n.t({
        id: "itemPageLayout.safetyElement.tab.service.label",
        message: "Service",
      }),
      icon: <Icon name="Wrench" size="medium" />,
      content: <ServicePlanFollowed />,
    },
    [BenefitTabs.ExchangePolicy]: {
      id: BenefitTabs.ExchangePolicy,
      trackingName: "Exchange policy tab",
      trackingId: "MO_ev44",
      label: i18n.t({
        id: "itemPageLayout.safetyElement.tab.exchangePolicy.label",
        message: "Exchange policy",
      }),
      icon: <Icon name="Exchange" size="medium" />,
      content: <ExchangePolicy ad={ad} />,
    },
    [BenefitTabs.Condition]: {
      id: BenefitTabs.Condition,
      trackingName: "Condition tab",
      trackingId: "MO_ev64",
      label: i18n.t({
        id: "itemPageLayout.common.condition",
        message: "Condition",
      }),
      icon: <Icon name="ActiveAds" size="medium" />,
      content: <Condition ad={ad} />,
    },
    [BenefitTabs.Premium]: {
      id: BenefitTabs.Premium,
      trackingName: "Premium tab",
      trackingId: "MO_ev46",
      label: i18n.t({
        id: "itemPageLayout.safetyElement.tab.premium.label",
        message: "Program car",
      }),
      icon: <Icon name="Car" size="medium" />,
      content: "premium" in ad && ad.premium ? <Premium premium={ad.premium} /> : null,
    },
    [BenefitTabs.HomeDelivery]: {
      id: BenefitTabs.HomeDelivery,
      trackingName: "Home delivery tab",
      trackingId: "MO_ev89",
      label: i18n.t({
        id: "itemPageLayout.safetyElement.tab.homeDelivery.label",
        message: "Home delivery",
      }),
      icon: <Icon name="DeliveryKey" size="medium" />,
      content: <HomeDelivery ad={ad} />,
    },
    // MEMBER ASSOCIATIONS
    [MemberAssociationTabs.TradeOrg]: {
      id: MemberAssociationTabs.TradeOrg,
      trackingName: "TradeOrg tab",
      trackingId: "MO_ev45",
      label: i18n.t({
        id: "itemPageLayout.safetyElement.tab.member.label",
        message: "Member",
      }),
      icon: <Icon name="StarCheck" size="medium" />,
      content: <TradeOrg adData={ad} />,
    },
    [MemberAssociationTabs.NbfMember]: {
      id: MemberAssociationTabs.NbfMember,
      trackingName: "TradeOrg tab",
      trackingId: "MO_ev45",
      label: i18n.t({
        id: "itemPageLayout.safetyElement.tab.member.label",
        message: "Member",
      }),
      icon: <Icon name="StarCheck" size="medium" />,
      content: <TradeOrg adData={ad} tradeOrg={MemberAssociationTabs.NbfMember} />,
    },
    [MemberAssociationTabs.MobilityPoolMember]: {
      id: MemberAssociationTabs.MobilityPoolMember,
      trackingName: "TradeOrg tab",
      trackingId: "MO_ev45",
      label: i18n.t({
        id: "itemPageLayout.safetyElement.tab.member.label",
        message: "Member",
      }),
      icon: <Icon name="StarCheck" size="medium" />,
      content: <TradeOrg adData={ad} tradeOrg={MemberAssociationTabs.MobilityPoolMember} />,
    },
    [MemberAssociationTabs.BruktbilgruppenMember]: {
      id: MemberAssociationTabs.BruktbilgruppenMember,
      trackingName: "TradeOrg tab",
      trackingId: "MO_ev45",
      label: i18n.t({
        id: "itemPageLayout.safetyElement.tab.member.label",
        message: "Member",
      }),
      icon: <Icon name="StarCheck" size="medium" />,
      content: <TradeOrg adData={ad} tradeOrg={MemberAssociationTabs.BruktbilgruppenMember} />,
    },
    [MemberAssociationTabs.NmcfMember]: {
      id: MemberAssociationTabs.NmcfMember,
      trackingName: "TradeOrg tab",
      trackingId: "MO_ev45",
      label: i18n.t({
        id: "itemPageLayout.safetyElement.tab.member.label",
        message: "Member",
      }),
      icon: <Icon name="StarCheck" size="medium" />,
      content: <TradeOrg adData={ad} tradeOrg={MemberAssociationTabs.NmcfMember} />,
    },
    [MemberAssociationTabs.NcbMember]: {
      id: MemberAssociationTabs.NcbMember,
      trackingName: "TradeOrg tab",
      trackingId: "MO_ev45",
      label: i18n.t({
        id: "itemPageLayout.safetyElement.tab.member.label",
        message: "Member",
      }),
      icon: <Icon name="StarCheck" size="medium" />,
      content: <TradeOrg adData={ad} tradeOrg={MemberAssociationTabs.NcbMember} />,
    },
  };
};

export const useSafetyElements = ({
  safetyElements,
  memberAssociations,
}: SafetyElementsProps): {
  id: SafetyTabs;
  trackingName: string;
  trackingId: string;
  label: string;
  icon: ReactNode;
  content: ReactNode;
}[] => {
  const ad = useAdViewAd();
  const tabMap = useTabMap(ad);

  const hasMemberAssociations = memberAssociations && memberAssociations.length > 0;

  const benefitElements = (safetyElements ?? [])
    .filter((element): element is BenefitTabs => Object.values(BenefitTabs).includes(element as BenefitTabs))
    .map((element) => tabMap[element]);
  const legacyMembershipElements = (safetyElements ?? [])
    .filter((element): element is MemberAssociationTabs =>
      Object.values(MemberAssociationTabs).includes(element as MemberAssociationTabs),
    )
    .map((element) => tabMap[element]);
  const membershipElement = hasMemberAssociations
    ? [
        {
          ...tabMap[MemberAssociationTabs.TradeOrg],
          content: <TradeOrg adData={ad} memberAssociations={memberAssociations} />,
        },
      ]
    : legacyMembershipElements;
  return [...benefitElements, ...membershipElement];
};
